import {
  Business,
  Close,
  PersonAdd,
  Public,
  Search,
} from "@mui/icons-material";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Pagination,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useState } from "react";
import LoadingButton from "../../../components/LoadingButton";
import { hostname, parseError } from "../../../utils";
import { useEmployeeTerminology } from "../../../utils/terminology";

interface PublicEmployee {
  id: string;
  name: string;
  title: string;
  description: string;
  picture: string;
  tags: string[];
  ownerCompany: {
    id: string;
    name: string;
    logoUrl?: string;
  };
}

interface PublicEmployeeMarketplaceDialogProps {
  open: boolean;
  onClose: () => void;
  companyId: string;
  getToken: () => Promise<string>;
  onEmployeeAdded: () => void;
  eventListener: (event: any) => void;
}

const PublicEmployeeMarketplaceDialog: React.FC<
  PublicEmployeeMarketplaceDialogProps
> = ({
  open,
  onClose,
  companyId,
  getToken,
  onEmployeeAdded,
  eventListener,
}) => {
  const {
    employee: employeeText,
    employees: employeesText,
    employeeLower,
    employeesLower,
  } = useEmployeeTerminology();
  const [employees, setEmployees] = useState<PublicEmployee[]>([]);
  const [loading, setLoading] = useState(false);
  const [addingEmployeeId, setAddingEmployeeId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState("");
  const perPage = 9;

  const fetchPublicEmployees = async () => {
    setLoading(true);
    setError("");

    try {
      const token = await getToken();
      const response = await axios.get(`${hostname}/public-employees`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          page,
          per_page: perPage,
          search: search.trim() || undefined,
          exclude_company_id: companyId, // Don't show our own employees
        },
      });

      setEmployees(response.data.employees || []);
      setTotal(response.data.total || 0);
    } catch (err: any) {
      setError(parseError(err));
    } finally {
      setLoading(false);
    }
  };

  const addEmployeeToCompany = async (employeeId: string) => {
    setAddingEmployeeId(employeeId);

    try {
      const token = await getToken();
      await axios.post(
        `${hostname}/companies/${companyId}/public-employees`,
        { publicEmployeeId: employeeId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      eventListener({
        type: "SET_SUCCESS",
        message: "Employee added to your company successfully!",
      });

      onEmployeeAdded();
      onClose(); // Close the dialog after successfully adding employee
    } catch (err: any) {
      eventListener({
        type: "SET_ERROR",
        error: parseError(err),
      });
    } finally {
      setAddingEmployeeId(null);
    }
  };

  useEffect(() => {
    if (open) {
      fetchPublicEmployees();
    }
  }, [open, page, search]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
    setPage(1); // Reset to first page when searching
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value);
  };

  const totalPages = Math.ceil(total / perPage);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: "85vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          pr: 6,
        }}
      >
        <Public color="primary" />
        Public {employeeText} Marketplace
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              Discover and add public {employeesLower} from other companies to
              your workspace. These {employeesLower} have been made available by
              their owners for public use. {employeesText} already added to your
              company are not shown.
            </Typography>
          </Alert>

          <TextField
            fullWidth
            variant="outlined"
            placeholder={`Search ${employeesLower} by name, title, or description...`}
            value={search}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 3 }}
          />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <Typography>Loading {employeesLower}...</Typography>
          </Box>
        ) : employees.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography variant="h6" color="text.secondary">
              {search
                ? `No ${employeesLower} found matching your search`
                : `No public ${employeesLower} available`}
            </Typography>
            {search && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Try adjusting your search terms
              </Typography>
            )}
          </Box>
        ) : (
          <>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {employees.map((employee) => (
                <Grid item xs={12} md={6} lg={4} key={employee.id}>
                  <Card
                    variant="outlined"
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 2 }}
                      >
                        <Avatar
                          src={employee.picture}
                          sx={{ width: 48, height: 48, mr: 2 }}
                        />
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Typography
                            variant="h6"
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {employee.name}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {employee.title}
                          </Typography>
                        </Box>
                      </Box>

                      {employee.description && (
                        <Tooltip
                          title={employee.description}
                          arrow
                          placement="top"
                          enterDelay={500}
                          componentsProps={{
                            tooltip: {
                              sx: {
                                fontSize: "0.875rem",
                                maxWidth: 500,
                              },
                            },
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              mb: 2,
                              height: 60,
                              overflow: "hidden",
                              display: "-webkit-box",
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: "vertical",
                            }}
                          >
                            {employee.description}
                          </Typography>
                        </Tooltip>
                      )}

                      <Divider sx={{ my: 2 }} />

                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 2 }}
                      >
                        <Business
                          sx={{ fontSize: 16, mr: 1, color: "text.secondary" }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          By {employee.ownerCompany.name}
                        </Typography>
                      </Box>

                      {employee.tags && employee.tags.length > 0 && (
                        <Stack
                          direction="row"
                          spacing={0.5}
                          sx={{ flexWrap: "wrap", gap: 0.5 }}
                        >
                          {employee.tags.slice(0, 3).map((tag) => (
                            <Chip key={tag} size="small" label={tag} />
                          ))}
                          {employee.tags.length > 3 && (
                            <Chip
                              size="small"
                              label={`+${employee.tags.length - 3} more`}
                              variant="outlined"
                            />
                          )}
                        </Stack>
                      )}
                    </CardContent>

                    <CardActions>
                      <LoadingButton
                        fullWidth
                        variant="contained"
                        startIcon={<PersonAdd />}
                        loading={addingEmployeeId === employee.id}
                        onClick={() => addEmployeeToCompany(employee.id)}
                      >
                        Add to Company
                      </LoadingButton>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {totalPages > 1 && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                />
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default PublicEmployeeMarketplaceDialog;
